import { motion } from 'framer-motion';
import { Info, TrendingDown } from 'lucide-react';
import { fmtNum } from '@/utils/format';

interface MacroCardProps {
  label: string;
  consumed: number;
  goal: number;
  color: 'emerald' | 'warn' | 'sky';
}

export function MacroCard({ label, consumed, goal, color }: MacroCardProps) {
  const safeGoal = goal > 0 ? goal : 1;
  const percent = (consumed / safeGoal) * 100;
  
  // Гибкие зоны (вместо жёсткого 105%):
  // 0-50% — мало (для белка важно)
  // 50-110% — норма (зелёная зона)
  // 110-130% — обрати внимание (янтарный, не красный)
  // 130%+ — заметно больше (всё ещё янтарный, но с подсказкой)
  
  const isLow = percent < 50 && label === 'Белки'; // только для белка
  const isOver = percent > 110;
  const isSignificantlyOver = percent > 130;
  
  // Базовые цвета — всегда в фирменной палитре
  const baseColors = {
    emerald: {
      progress: 'bg-emerald-glow',
      text: 'text-emerald-glow',
      border: 'border-emerald-glow/20',
      glow: 'shadow-[0_0_8px_rgba(16,229,142,0.55)]',
    },
    warn: {
      progress: 'bg-warn',
      text: 'text-warn',
      border: 'border-warn/20',
      // Свечение было rgba(245,158,11) при заливке #FFB454 — ореол не совпадал
      // с самой полосой, та же болезнь, что была у кольца калорий
      glow: 'shadow-[0_0_8px_rgba(255,180,84,0.55)]',
    },
    sky: {
      progress: 'bg-sky-glow',
      text: 'text-sky-glow',
      border: 'border-sky-glow/20',
      glow: 'shadow-[0_0_8px_rgba(61,216,255,0.55)]',
    },
  };
  
  // Состояние карточки
  let state = baseColors[color];
  let showBadge: 'over' | 'low' | null = null;
  
  if (isOver) {
    // Тот же цвет превышения, что у кольца калорий (токен `over` в tailwind.config)
    state = {
      progress: 'bg-over',
      text: 'text-over',
      border: 'border-over/30 bg-over/[0.03]',
      glow: 'shadow-[0_0_8px_rgba(232,168,92,0.55)]',
    };
    showBadge = 'over';
  } else if (isLow) {
    showBadge = 'low';
  }
  
  return (
    <div className={`card flex-1 p-3 relative transition-colors ${state.border}`}>
      {/* Мягкий бейдж "Внимание" вместо красного "Лимит" */}
      {showBadge === 'over' && (
        <div className="absolute -top-1.5 -right-1.5 bg-over/90 text-bg text-[9px] font-medium px-1.5 py-0.5 rounded-full flex items-center gap-0.5 shadow-sm">
          <Info size={9} />
          Внимание
        </div>
      )}
      
      {/* Бейдж "Мало" для недобора белка */}
      {showBadge === 'low' && (
        <div className="absolute -top-1.5 -right-1.5 bg-sky-glow/90 text-bg text-[9px] font-medium px-1.5 py-0.5 rounded-full flex items-center gap-0.5 shadow-sm">
          <TrendingDown size={9} />
          Мало
        </div>
      )}
      
      <div className="text-[11px] text-ink-dim mb-1">{label}</div>
      <div className="flex items-baseline gap-1 mb-2">
        <span className={`font-display font-semibold text-base ${state.text}`}>
          {fmtNum(consumed, 0)}
        </span>
        <span className="text-[10px] text-ink-dim">/ {fmtNum(goal, 0)}г</span>
      </div>
      
      {/* Прогресс-бар */}
      <div className="h-1.5 bg-line rounded-full overflow-hidden">
        <motion.div
          className={`h-full rounded-full ${state.progress} ${state.glow}`}
          initial={{ width: 0 }}
          animate={{ width: `${Math.min(percent, 100)}%` }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        />
      </div>
      
      {/* Мягкие подсказки внизу */}
      {isSignificantlyOver && (
        <div className="text-[9px] text-over/80 mt-1 leading-tight">
          сегодня выше нормы
        </div>
      )}
      {isOver && !isSignificantlyOver && (
        <div className="text-[9px] text-over/80 mt-1 leading-tight">
          чуть больше нормы
        </div>
      )}
      {/* Текстовая подсказка «добери белок» убрана 03.09.2026: о том, ЧТО делать,
          теперь говорит строка Славы одной фразой на весь экран. Бейдж «Мало»
          остаётся — это сигнал, а не голос. Два голоса в разных карточках
          читались как разнобой. */}
    </div>
  );
}
